import { ComponentPropsWithoutRef, forwardRef } from "react";
import cx from "classnames";

import styles from "./Dialog.module.scss";

/**
 * Props for the DialogFooter component
 * @extends ComponentPropsWithoutRef<"footer">
 */
export type DialogFooterProps = ComponentPropsWithoutRef<"footer"> & {
  /**
   * @default false
   */
  sticky?: boolean;
};

/**
 * DialogFooter component for the footer section of a Dialog.
 *
 * Features:
 * - Container for dialog action buttons
 * - Optional sticky positioning for long content
 * - Consistent spacing and alignment
 * - Right-aligned button layout by default
 * - Flexible content area for custom layouts
 *
 * @example
 * <Dialog.Footer sticky>
 *   <Dialog.CancelButton>Cancel</Dialog.CancelButton>
 *   <Button appearance="primary">Save</Button>
 * </Dialog.Footer>
 */
export const DialogFooter = forwardRef<HTMLElement, DialogFooterProps>(
  (props, ref) => {
    const { children, className, sticky = false, ...rest } = props;

    const footerClassNames = cx(styles["footer"], className, {
      [styles["sticky"]]: sticky,
    });

    return (
      <footer
        className={footerClassNames}
        data-anv="dialog-footer"
        ref={ref}
        {...rest}
      >
        {children}
      </footer>
    );
  },
);

DialogFooter.displayName = "DialogFooter";
